import { randomUuid } from '@kinshipos/shared-utils';
import { useAuthStore } from '../auth/auth.store';

/**
 * API client.
 *
 * Wraps `fetch` with the four things every call needs and none should repeat:
 * the auth header, the response-envelope unwrap, transparent token refresh,
 * and a typed error the UI can switch on.
 */

const BASE_URL: string = import.meta.env['VITE_API_BASE_URL'] ?? '';

export interface ApiErrorDetail {
  field?: string;
  message: string;
  code?: string;
}

/** Mirrors the server's error envelope. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: ApiErrorDetail[];
  readonly meta: Record<string, unknown>;
  readonly requestId: string | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details: ApiErrorDetail[] = [],
    meta: Record<string, unknown> = {},
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.meta = meta;
    this.requestId = requestId;
  }

  /** True when the society's plan does not include the feature (402). */
  get isPlanGate(): boolean {
    return this.code === 'FEATURE_NOT_IN_PLAN' || this.code === 'USAGE_LIMIT_EXCEEDED';
  }

  /** True when the society is read-only because its subscription lapsed. */
  get isReadOnly(): boolean {
    return this.code === 'TENANT_READ_ONLY' || this.code === 'TENANT_SUSPENDED';
  }

  /** Field-keyed messages, ready for react-hook-form's `setError`. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const detail of this.details) {
      if (!detail.field) continue;
      // Strip the `body.` / `query.` prefix the server adds — form fields are
      // named without it.
      out[detail.field.replace(/^(body|query|params)\./, '')] = detail.message;
    }
    return out;
  }
}

interface SuccessEnvelope<T> {
  success: true;
  data: T;
  meta: { requestId: string; pagination?: PaginationMeta };
}

interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string; details?: ApiErrorDetail[]; meta?: Record<string, unknown> };
  meta: { requestId: string };
}

export interface PaginationMeta {
  nextCursor: string | null;
  total?: number;
  limit: number;
  hasMore: boolean;
}

export interface Page<T> {
  items: T[];
  pagination: PaginationMeta;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Required on payment routes; generated automatically for other mutations. */
  idempotencyKey?: string;
  /** Skips the auth header — used by the public signup and plan routes. */
  anonymous?: boolean;
  query?: Record<string, string | number | boolean | undefined | null>;
}

/**
 * Single-flight token refresh.
 *
 * Without this, a page that fires six queries on mount would hit an expired
 * token six times and issue six refreshes — five of which would be rejected as
 * token reuse and log the user out.
 */
let refreshPromise: Promise<boolean> | null = null;

async function refreshOnce(): Promise<boolean> {
  refreshPromise ??= useAuthStore
    .getState()
    .refresh()
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(path.startsWith('http') ? path : `${BASE_URL}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

async function request<T>(path: string, options: RequestOptions = {}, isRetry = false): Promise<T> {
  const { body, idempotencyKey, anonymous, query, headers, ...rest } = options;
  const method = (rest.method ?? 'GET').toUpperCase();

  const finalHeaders = new Headers(headers);
  finalHeaders.set('Accept', 'application/json');
  // A FormData body sets its own multipart boundary; anything else is JSON.
  const multipart = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !multipart) finalHeaders.set('Content-Type', 'application/json');

  if (!anonymous) {
    const token = useAuthStore.getState().accessToken;
    if (token) finalHeaders.set('Authorization', `Bearer ${token}`);
  }

  // Every mutation gets a key. A double-clicked Save must not create two rows,
  // and asking each call site to remember is how that guarantee gets lost.
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    finalHeaders.set('Idempotency-Key', idempotencyKey ?? randomUuid());
  }

  finalHeaders.set('X-App-Version', import.meta.env['VITE_APP_VERSION'] ?? 'dev');
  finalHeaders.set('X-Device-Id', deviceId());

  const response = await fetch(buildUrl(path, query), {
    ...rest,
    method,
    headers: finalHeaders,
    body: body === undefined ? undefined : multipart ? body : JSON.stringify(body),
  });

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as
    | SuccessEnvelope<T>
    | ErrorEnvelope
    | null;

  if (!payload) {
    throw new ApiError(response.status, 'INTERNAL_ERROR', 'The server sent an unreadable response.');
  }

  if (payload.success) return payload.data;

  // One transparent refresh-and-retry on an expired token; anything more would
  // mask a genuine auth failure behind a retry loop.
  if (
    (payload.error.code === 'AUTH_TOKEN_EXPIRED' || response.status === 401) &&
    !isRetry &&
    !anonymous
  ) {
    if (await refreshOnce()) {
      return request<T>(path, options, true);
    }
    useAuthStore.getState().signOut();
  }

  throw new ApiError(
    response.status,
    payload.error.code,
    payload.error.message,
    payload.error.details ?? [],
    payload.error.meta ?? {},
    payload.meta.requestId,
  );
}

/**
 * POSTs and saves the response as a file.
 *
 * A plain `<a href>` cannot be used for these: every API call is authorised by
 * a bearer token in a header, and a browser navigation sends no headers. So the
 * bytes are fetched here and handed to a temporary object URL.
 *
 * The filename comes from the server's Content-Disposition — it is the server
 * that knows the format actually produced, which matters when a PDF falls back
 * to HTML because Puppeteer is not installed.
 */
async function download(path: string, body: unknown, fallbackName: string): Promise<void> {
  const finalHeaders = new Headers({ Accept: '*/*', 'Content-Type': 'application/json' });

  const token = useAuthStore.getState().accessToken;
  if (token) finalHeaders.set('Authorization', `Bearer ${token}`);
  finalHeaders.set('Idempotency-Key', randomUuid());
  finalHeaders.set('X-App-Version', import.meta.env['VITE_APP_VERSION'] ?? 'dev');
  finalHeaders.set('X-Device-Id', deviceId());

  const response = await fetch(buildUrl(path), {
    method: 'POST',
    headers: finalHeaders,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    // A failure still comes back as the normal envelope, so the real message
    // reaches the user rather than "download failed".
    const payload = (await response.json().catch(() => null)) as ErrorEnvelope | null;
    throw new ApiError(
      response.status,
      payload?.error.code ?? 'INTERNAL_ERROR',
      payload?.error.message ?? 'The export could not be produced.',
    );
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const matched = /filename="([^"]+)"/.exec(disposition);
  const filename = matched?.[1] ?? fallbackName;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Revoked on the next tick: revoking synchronously can cancel the download
    // in Safari before it has read the blob.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/** Same as `request`, but keeps the pagination envelope. */
async function requestPage<T>(path: string, options: RequestOptions = {}): Promise<Page<T>> {
  // A GET has no body and needs no idempotency key; both are destructured away
  // so they cannot leak into the fetch init.
  const { body: _body, idempotencyKey: _idempotencyKey, anonymous, query, headers, ...rest } = options;

  const finalHeaders = new Headers(headers);
  finalHeaders.set('Accept', 'application/json');
  if (!anonymous) {
    const token = useAuthStore.getState().accessToken;
    if (token) finalHeaders.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(buildUrl(path, query), { ...rest, headers: finalHeaders });
  const payload = (await response.json()) as SuccessEnvelope<T[]> | ErrorEnvelope;

  if (!payload.success) {
    if (response.status === 401 && !anonymous && (await refreshOnce())) {
      return requestPage<T>(path, options);
    }
    throw new ApiError(
      response.status,
      payload.error.code,
      payload.error.message,
      payload.error.details ?? [],
      payload.error.meta ?? {},
      payload.meta.requestId,
    );
  }

  return {
    items: payload.data,
    pagination: payload.meta.pagination ?? { nextCursor: null, limit: payload.data.length, hasMore: false },
  };
}

/** Stable per-browser id used for session device binding. */
function deviceId(): string {
  const KEY = 'kinshipos.deviceId';
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = randomUuid();
    localStorage.setItem(KEY, id);
  }
  return id;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  getPage: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    requestPage<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),

  download,
};
